import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trophy, Plus, Undo2, X, Play, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { RoundScoreDialog } from '@/components/scoring/RoundScoreDialog';
import { RoundsTable } from '@/components/scoring/RoundsTable';
import { useMatchStore } from '@/stores/matchStore';
import { useToast } from '@/hooks/use-toast';
import { useWakeLock } from '@/hooks/useWakeLock';
import { MATCH_FORMAT_CONFIG } from '@/types';
import type { RoundScore, Round, TrumpSuit } from '@/types';
import { roundRepo } from '@/db/repositories/roundRepo';

export function ActiveMatchPage() {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    activeMatch,
    isLoading,
    error,
    loadActiveMatch,
    addRound,
    deleteLastRound,
    startNextGame,
    abandonMatch,
    clearError,
  } = useMatchStore();

  const [isScoreDialogOpen, setIsScoreDialogOpen] = useState(false);
  const [isAbandonDialogOpen, setIsAbandonDialogOpen] = useState(false);
  const [expandedGames, setExpandedGames] = useState<Set<string>>(new Set());
  const [gameRounds, setGameRounds] = useState<Record<string, Round[]>>({});

  const { request: requestWakeLock, release: releaseWakeLock } = useWakeLock();

  // Load match and request wake lock
  useEffect(() => {
    if (matchId) {
      loadActiveMatch(matchId);
      requestWakeLock();
    }

    return () => {
      releaseWakeLock();
    };
  }, [matchId, loadActiveMatch, requestWakeLock, releaseWakeLock]);

  // Pre-load rounds for all completed games when match changes
  useEffect(() => {
    const loadCompletedGameRounds = async () => {
      if (!activeMatch) return;

      const completedGames = activeMatch.games.filter(
        (g) => g.status === 'completed' && !gameRounds[g.id]
      );

      for (const game of completedGames) {
        try {
          const rounds = await roundRepo.getByGameId(game.id);
          setGameRounds((prev) => ({ ...prev, [game.id]: rounds }));
        } catch (err) {
          console.error('Failed to load rounds for game:', game.id, err);
        }
      }
    };

    loadCompletedGameRounds();
  }, [activeMatch?.games.length, activeMatch?.id]);

  useEffect(() => {
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error,
      });
      clearError();
    }
  }, [error, toast, clearError]);

  const handleAddRound = async (
    scores: RoundScore[],
    trumpSuit: TrumpSuit,
    trumpCallerId: string
  ) => {
    if (!activeMatch?.currentGame) return;

    const success = await addRound(scores, trumpSuit, trumpCallerId);
    if (success) {
      toast({
        title: 'Round added',
        description: `Round ${activeMatch.currentGame.rounds.length + 1} recorded.`,
      });
      setIsScoreDialogOpen(false);
    }
  };

  const handleUndoLastRound = async () => {
    const success = await deleteLastRound();
    if (success) {
      toast({
        title: 'Round removed',
        description: 'Last round has been deleted.',
      });
    }
  };

  const handleStartNextGame = async () => {
    const game = await startNextGame();
    if (game) {
      toast({
        title: 'New game started',
        description: `Game ${game.gameNumber} has begun!`,
      });
    }
  };

  const handleAbandonMatch = async () => {
    const success = await abandonMatch();
    if (success) {
      toast({
        title: 'Match abandoned',
        description: 'The match has been deleted.',
      });
      navigate('/');
    }
    setIsAbandonDialogOpen(false);
  };

  const toggleGameExpansion = async (gameId: string) => {
    const newExpanded = new Set(expandedGames);
    if (newExpanded.has(gameId)) {
      newExpanded.delete(gameId);
    } else {
      newExpanded.add(gameId);
      // Load rounds for this game if not already loaded
      if (!gameRounds[gameId]) {
        try {
          const rounds = await roundRepo.getByGameId(gameId);
          setGameRounds((prev) => ({ ...prev, [gameId]: rounds }));
        } catch (err) {
          console.error('Failed to load rounds:', err);
        }
      }
    }
    setExpandedGames(newExpanded);
  };

  if (isLoading || !activeMatch) {
    return (
      <div className="container py-6">
        <div className="flex items-center justify-center h-40">
          <p className="text-muted-foreground">Loading match...</p>
        </div>
      </div>
    );
  }

  const formatConfig = MATCH_FORMAT_CONFIG[activeMatch.format];

  // Match completed view
  if (activeMatch.status === 'completed') {
    const winner = activeMatch.participants.find(
      (p) => p.id === activeMatch.winnerId
    );

    return (
      <div className="container py-6 space-y-6">
        <Card className="bg-gradient-to-br from-yellow-100 to-yellow-50 dark:from-yellow-900/20 dark:to-yellow-800/10 border-yellow-200 dark:border-yellow-800">
          <CardContent className="py-8 text-center">
            <Trophy className="h-16 w-16 mx-auto mb-4 text-yellow-500" />
            <h2 className="text-2xl font-bold mb-2">Match Complete!</h2>
            {winner && (
              <p className="text-xl">
                <span className="font-semibold">{winner.name}</span> wins the
                match!
              </p>
            )}
            <p className="text-muted-foreground mt-2">
              {formatConfig.label}
            </p>
          </CardContent>
        </Card>

        {/* Final Game Scores */}
        <Card>
          <CardHeader>
            <CardTitle>Final Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activeMatch.participants.map((participant) => {
                const wins = activeMatch.gameWins[participant.id] || 0;
                const isWinner = participant.id === activeMatch.winnerId;
                return (
                  <div
                    key={participant.id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      isWinner ? 'bg-yellow-100 dark:bg-yellow-900/20' : 'bg-muted/50'
                    }`}
                  >
                    <span className={isWinner ? 'font-semibold' : ''}>
                      {participant.name}
                    </span>
                    <span className="text-2xl font-bold">{wins}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Games Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Games Played ({activeMatch.games.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {activeMatch.games.map((game) => {
                const winner = activeMatch.participants.find(
                  (p) => p.id === game.winnerId
                );
                return (
                  <div
                    key={game.id}
                    className="flex items-center justify-between py-2 border-b last:border-0"
                  >
                    <span className="text-sm text-muted-foreground">
                      Game {game.gameNumber}
                    </span>
                    <span className="font-medium">{winner?.name}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Button className="w-full" onClick={() => navigate('/')}>
          Back to Home
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => navigate(`/matches/${activeMatch.id}`)}
        >
          View Details
        </Button>
      </div>
    );
  }

  // Check if we need to start next game (current game completed but match not)
  const needsNextGame =
    !activeMatch.currentGame || activeMatch.currentGame.status === 'completed';
  const lastCompletedGame = needsNextGame
    ? activeMatch.games.find((g) => g.status === 'completed')
    : null;

  // Between games view
  if (needsNextGame) {
    return (
      <div className="container py-6 space-y-6">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/matches')}
          className="-ml-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Matches
        </Button>

        {/* Match Progress */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{formatConfig.label}</span>
              <span className="text-sm font-normal text-muted-foreground">
                First to {formatConfig.gamesToWin}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activeMatch.participants.map((participant) => {
                const wins = activeMatch.gameWins[participant.id] || 0;
                return (
                  <div key={participant.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{participant.name}</span>
                      <div className="flex items-center gap-2">
                        {Array.from({ length: formatConfig.gamesToWin }).map(
                          (_, i) => (
                            <div
                              key={i}
                              className={`w-4 h-4 rounded-full ${
                                i < wins
                                  ? 'bg-primary'
                                  : 'bg-muted border border-border'
                              }`}
                            />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Last Game Result */}
        {lastCompletedGame && (
          <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <CardContent className="py-6 text-center">
              <h3 className="font-semibold mb-2">
                Game {lastCompletedGame.gameNumber} Complete!
              </h3>
              <p>
                {
                  activeMatch.participants.find(
                    (p) => p.id === lastCompletedGame.winnerId
                  )?.name
                }{' '}
                won the game
              </p>
            </CardContent>
          </Card>
        )}

        {/* Games List with Round Details */}
        {activeMatch.games.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Games ({activeMatch.games.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {activeMatch.games.map((game) => {
                const isExpanded = expandedGames.has(game.id);
                const winner = activeMatch.participants.find(
                  (p) => p.id === game.winnerId
                );
                // Use currentGame rounds if this is the recently completed game
                const rounds =
                  activeMatch.currentGame?.id === game.id &&
                  activeMatch.currentGame.rounds.length > 0
                    ? activeMatch.currentGame.rounds
                    : gameRounds[game.id] || [];

                return (
                  <div
                    key={game.id}
                    className="border rounded-lg overflow-hidden"
                  >
                    {/* Game Header */}
                    <button
                      className="w-full flex items-center justify-between p-3 hover:bg-accent/50 transition-colors"
                      onClick={() => toggleGameExpansion(game.id)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          Game {game.gameNumber}
                        </span>
                        {game.status === 'completed' && winner && (
                          <span className="text-xs text-muted-foreground">
                            - {winner.name} won
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </button>

                    {/* Expanded Round Details */}
                    {isExpanded && (
                      <div className="border-t bg-muted/30 p-2">
                        {rounds.length === 0 && !gameRounds[game.id] ? (
                          <p className="text-sm text-muted-foreground text-center py-2">
                            Loading rounds...
                          </p>
                        ) : (
                          <RoundsTable
                            rounds={rounds}
                            participants={game.participants}
                            targetScore={game.targetScore}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Start Next Game Button */}
        <Button
          size="lg"
          className="w-full h-14 text-lg"
          onClick={handleStartNextGame}
        >
          <Play className="mr-2 h-5 w-5" />
          Start Game {activeMatch.games.length + 1}
        </Button>

        <Button
          variant="outline"
          className="w-full"
          onClick={() => setIsAbandonDialogOpen(true)}
        >
          <X className="mr-2 h-4 w-4" />
          Abandon Match
        </Button>

        {/* Abandon Confirmation Dialog */}
        <Dialog
          open={isAbandonDialogOpen}
          onOpenChange={setIsAbandonDialogOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Abandon Match?</DialogTitle>
              <DialogDescription>
                This will permanently delete the match and all its games. This
                action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsAbandonDialogOpen(false)}
              >
                Keep Playing
              </Button>
              <Button variant="destructive" onClick={handleAbandonMatch}>
                Abandon Match
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Active game view
  const currentGame = activeMatch.currentGame;

  // Guard should never be reached due to needsNextGame check above, but TypeScript needs this
  if (!currentGame) {
    return null;
  }

  return (
    <div className="container py-6 space-y-6">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/matches')}
        className="-ml-2"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Matches
      </Button>

      {/* Match Progress Header */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {formatConfig.shortLabel} - Game {currentGame.gameNumber}
            </span>
            <div className="flex items-center gap-4">
              {activeMatch.participants.map((participant) => {
                const wins = activeMatch.gameWins[participant.id] || 0;
                return (
                  <div key={participant.id} className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">
                      {participant.type === 'team'
                        ? participant.name
                        : participant.name.split(' ')[0]}
                      :
                    </span>
                    <span className="font-bold">{wins}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Game Progress */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Current Game Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {currentGame.participants.map((participant) => {
            const totalData = currentGame.totals.find(
              (t) => t.participantId === participant.id
            );
            const total = totalData?.totalPoints || 0;
            const progress = Math.min((total / currentGame.targetScore) * 100, 100);
            const remaining = Math.max(0, currentGame.targetScore - total);
            const isLeading =
              currentGame.totals.length > 0 &&
              total === Math.max(...currentGame.totals.map((t) => t.totalPoints));

            return (
              <div key={participant.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className={`font-medium ${isLeading ? 'text-primary' : ''}`}>
                    {participant.type === 'team'
                      ? participant.name
                      : participant.name.split(' ')[0]}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={`text-2xl font-bold ${isLeading ? 'text-primary' : ''}`}>
                      {total}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      / {currentGame.targetScore}
                    </span>
                  </div>
                </div>
                <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`absolute inset-y-0 left-0 rounded-full transition-all duration-300 ${
                      isLeading ? 'bg-primary' : 'bg-muted-foreground/50'
                    }`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="flex justify-end">
                  <span className="text-xs text-muted-foreground">
                    {remaining} to go
                  </span>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Games List with Round Details */}
      <Card>
        <CardHeader>
          <CardTitle>Games ({activeMatch.games.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {activeMatch.games.map((game) => {
            const isExpanded = expandedGames.has(game.id);
            const isCurrentGame = game.id === currentGame.id;
            const winner = activeMatch.participants.find(
              (p) => p.id === game.winnerId
            );
            const rounds = isCurrentGame
              ? currentGame.rounds
              : gameRounds[game.id] || [];

            return (
              <div
                key={game.id}
                className={`border rounded-lg overflow-hidden ${
                  isCurrentGame ? 'border-primary' : ''
                }`}
              >
                {/* Game Header */}
                <button
                  className="w-full flex items-center justify-between p-3 hover:bg-accent/50 transition-colors"
                  onClick={() => toggleGameExpansion(game.id)}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Game {game.gameNumber}</span>
                    {isCurrentGame && (
                      <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded">
                        Current
                      </span>
                    )}
                    {game.status === 'completed' && winner && (
                      <span className="text-xs text-muted-foreground">
                        - {winner.name} won
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isCurrentGame && (
                      <span className="text-sm text-muted-foreground">
                        {currentGame.rounds.length} rounds
                      </span>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </button>

                {/* Expanded Round Details */}
                {isExpanded && (
                  <div className="border-t bg-muted/30 p-2">
                    <RoundsTable
                      rounds={rounds}
                      participants={game.participants}
                      targetScore={game.targetScore}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="space-y-3">
        <Button
          size="lg"
          className="w-full h-14 text-lg"
          onClick={() => setIsScoreDialogOpen(true)}
        >
          <Plus className="mr-2 h-5 w-5" />
          Add Round
        </Button>

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleUndoLastRound}
            disabled={currentGame.rounds.length === 0}
          >
            <Undo2 className="mr-2 h-4 w-4" />
            Undo Last
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setIsAbandonDialogOpen(true)}
          >
            <X className="mr-2 h-4 w-4" />
            Abandon
          </Button>
        </div>
      </div>

      {/* Score Entry Dialog */}
      <RoundScoreDialog
        open={isScoreDialogOpen}
        onOpenChange={setIsScoreDialogOpen}
        participants={currentGame.participants}
        mode={currentGame.mode}
        onSave={handleAddRound}
      />

      {/* Abandon Confirmation Dialog */}
      <Dialog open={isAbandonDialogOpen} onOpenChange={setIsAbandonDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Abandon Match?</DialogTitle>
            <DialogDescription>
              This will permanently delete the match and all its games. This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAbandonDialogOpen(false)}
            >
              Keep Playing
            </Button>
            <Button variant="destructive" onClick={handleAbandonMatch}>
              Abandon Match
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
